// Undated board PREFERENCES — "after a hard refresh, come back to what I was doing".
//
// Deliberately separate from the per-day overlays (oc-...-<date>). These keys carry NO date suffix,
// which is exactly what keeps clearAllDayOverlays (boardViewHelpers) from wiping them when a day is
// re-simulated: it only removes keys ending in a date. A preference is not day data.
//
// The tabs themselves need nothing here — they are URL routes (/board, /crnas, ...), so a refresh
// already lands on the same tab. The selected date persists elsewhere.
//
// Every read is defensive: an unknown enum value, a non-array payload, junk JSON, or unavailable
// storage (private mode throws) all fall back to the shipped default instead of poisoning the view.

export const BOARD_PREF_KEYS = {
  viewMode: 'oc-board-view-mode',
  shiftsView: 'oc-board-shifts-view',
  locations: 'oc-board-locations',
  forcedSites: 'oc-board-forced-sites',
  legendOpen: 'oc-board-legend-open',
  sitesOpen: 'oc-board-sites-open',
  bullpenOpen: 'oc-board-bullpen-open',
  sitesWidth: 'oc-board-sites-width',
  bullpenWidth: 'oc-board-bullpen-width',
} as const;

const read = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const write = (key: string, value: string): void => {
  try { localStorage.setItem(key, value); } catch { /* private mode — keep the in-memory value */ }
};

export type BoardViewMode = 'active' | 'all';
const VIEW_MODES: readonly string[] = ['active', 'all'];
export const loadViewMode = (): BoardViewMode => {
  const v = read(BOARD_PREF_KEYS.viewMode);
  return v && VIEW_MODES.includes(v) ? (v as BoardViewMode) : 'active';
};
export const saveViewMode = (v: BoardViewMode): void => write(BOARD_PREF_KEYS.viewMode, v);

export type ShiftsView = 'available' | 'allocated' | 'all';
const SHIFTS_VIEWS: readonly string[] = ['available', 'allocated', 'all'];
export const loadShiftsView = (): ShiftsView => {
  const v = read(BOARD_PREF_KEYS.shiftsView);
  return v && SHIFTS_VIEWS.includes(v) ? (v as ShiftsView) : 'available';
};
export const saveShiftsView = (v: ShiftsView): void => write(BOARD_PREF_KEYS.shiftsView, v);

// Hospital-id sets (the LOCATION filter; the force-shown sites), stored as a JSON array of strings.
// An EMPTY set is meaningful for locations — it means "all locations" — so it round-trips as [].
const loadIdSet = (key: string): Set<string> => {
  const raw = read(key);
  if (!raw) return new Set<string>();
  try {
    const v = JSON.parse(raw);
    if (!Array.isArray(v)) return new Set<string>();
    return new Set<string>(v.filter((x: unknown): x is string => typeof x === 'string'));
  } catch { return new Set<string>(); }
};
const saveIdSet = (key: string, ids: ReadonlySet<string>): void => write(key, JSON.stringify([...ids]));

export const loadSelectedHospitalIds = (): Set<string> => loadIdSet(BOARD_PREF_KEYS.locations);
export const saveSelectedHospitalIds = (ids: ReadonlySet<string>): void => saveIdSet(BOARD_PREF_KEYS.locations, ids);
/**
 * Drop saved site ids that no longer name a known hospital.
 *
 * These sets are undated and outlive the data they point at: a site gets deleted, or a different
 * account signs in on the same browser (localStorage is per-origin, not per-tenant). An unknown id in
 * the LOCATION filter is worse than useless -- the filter is non-empty, so it is no longer "all
 * locations", yet it matches nothing, and the board renders NO sites with nothing to say why.
 *
 * Returns `selected` itself when nothing was dropped, so a React state setter can bail out of the
 * re-render. Pruning everything yields the empty set, which IS "all locations". An empty `knownIds`
 * means the hospitals have not loaded yet, so nothing is judged unknown.
 */
export const pruneUnknownHospitalIds = <T extends ReadonlySet<string>>(
  selected: T,
  knownIds: Iterable<string>,
): T | Set<string> => {
  const known = new Set(knownIds);
  if (known.size === 0 || selected.size === 0) return selected;
  const kept = new Set([...selected].filter(id => known.has(id)));
  return kept.size === selected.size ? selected : kept;
};

export const loadForcedHospitalIds = (): Set<string> => loadIdSet(BOARD_PREF_KEYS.forcedSites);
export const saveForcedHospitalIds = (ids: ReadonlySet<string>): void => saveIdSet(BOARD_PREF_KEYS.forcedSites, ids);

/** The legend ships CLOSED, so only an explicit 'true' reopens it. */
export const loadLegendOpen = (): boolean => read(BOARD_PREF_KEYS.legendOpen) === 'true';
export const saveLegendOpen = (open: boolean): void => write(BOARD_PREF_KEYS.legendOpen, String(open));

// The two side bars (Sites, Bullpen) ship OPEN: `!== 'false'` so a missing or unreadable key still
// opens them, and only an explicit collapse is remembered. Not date-keyed, or the bars would flip
// open/closed as the board's date changes.
export const loadSitesOpen = (): boolean => read(BOARD_PREF_KEYS.sitesOpen) !== 'false';
export const saveSitesOpen = (open: boolean): void => write(BOARD_PREF_KEYS.sitesOpen, String(open));
export const loadBullpenOpen = (): boolean => read(BOARD_PREF_KEYS.bullpenOpen) !== 'false';
export const saveBullpenOpen = (open: boolean): void => write(BOARD_PREF_KEYS.bullpenOpen, String(open));

// The two side bars can also be dragged to a custom width (SidebarResizeHandle). Each width is clamped
// to its bar's range, so a junk or out-of-date value can never squeeze the board or hide a bar.
export interface WidthRange { min: number; max: number; default: number }
export const SITES_WIDTH: WidthRange = { min: 200, max: 520, default: 260 };
export const BULLPEN_WIDTH: WidthRange = { min: 300, max: 760, default: 420 };

export const clampWidth = (width: number, range: WidthRange): number =>
  Number.isFinite(width) ? Math.round(Math.min(range.max, Math.max(range.min, width))) : range.default;

const loadWidth = (key: string, range: WidthRange): number => {
  const raw = read(key);
  if (raw === null || raw.trim() === '') return range.default;
  return clampWidth(Number(raw), range);
};
export const loadSitesWidth = (): number => loadWidth(BOARD_PREF_KEYS.sitesWidth, SITES_WIDTH);
export const saveSitesWidth = (width: number): void => write(BOARD_PREF_KEYS.sitesWidth, String(width));
export const loadBullpenWidth = (): number => loadWidth(BOARD_PREF_KEYS.bullpenWidth, BULLPEN_WIDTH);
export const saveBullpenWidth = (width: number): void => write(BOARD_PREF_KEYS.bullpenWidth, String(width));

/** The board column never gets narrower than this when a side bar is dragged wider. */
export const MIN_BOARD_WIDTH = 480;
const RAIL_WIDTH = 40;
const LAYOUT_GAPS = 40; // two 20px grid gaps

/**
 * The widest a side bar may be dragged in a layout `layoutWidth` px wide, given the other bar's current
 * width (or null when it is collapsed to its rail): whatever keeps MIN_BOARD_WIDTH for the board, but
 * never below the bar's own minimum. An unmeasured layout (0) leaves only the range's own maximum.
 */
export const maxSidebarWidth = (range: WidthRange, layoutWidth: number, otherBarWidth: number | null): number => {
  if (!layoutWidth) return range.max;
  const room = layoutWidth - LAYOUT_GAPS - (otherBarWidth ?? RAIL_WIDTH) - MIN_BOARD_WIDTH;
  return Math.max(range.min, Math.min(range.max, Math.floor(room)));
};
